import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { InlineError, Skeleton } from '../../components/ui';
import { createPublicSupabaseClient, type SupabaseClient } from '../../lib/supabase';
import {
  HuddleError,
  loadLinkedInFunnel,
  type LinkedInFunnel,
  type LinkedInTier,
  type LinkedInTierRow,
  type LinkedInWeek,
} from './huddleApi';
import { weekLabel } from '../tracker/linkedinScoreboard';
import { currentMessageLabel, messageRate, messageVersionLabel } from '../tracker/messageReport';
import './huddle.css';
import './linkedinFunnel.css';

const tier2Steps: [keyof LinkedInTierRow, string][] = [
  ['messaged', 'Messaged'],
  ['replied', 'Replied'],
  ['info_replies', 'Info reply'],
  ['overviews_sent', 'Overview sent'],
  ['promoted', 'Promoted to lead'],
  ['calls_booked', 'Call booked'],
  ['calls_held', 'Call held'],
  ['proposals_sent', 'Proposal sent'],
  ['deals_won', 'Deals won'],
];
const tier1Steps: [keyof LinkedInTierRow, string][] = [
  ['requested', 'Request sent'],
  ['accepted', 'Accepted'],
  ['dm1', 'DM 1'],
  ['dm2', 'DM 2'],
  ['dm3', 'DM 3'],
  ['replied', 'Replied'],
  ['calls_booked', 'Call booked'],
  ['calls_held', 'Call held'],
  ['proposals_sent', 'Proposal sent'],
  ['deals_won', 'Won'],
];
const weekSteps: [keyof LinkedInWeek, string][] = [
  ['dms_sent', 'DMs sent'],
  ['replies', 'Replies'],
  ['info_replies', 'Info replies'],
  ['overviews_sent', 'Overviews sent'],
  ['promoted_to_lead', 'Promoted to lead'],
  ['calls_booked', 'Calls booked'],
  ['calls_held', 'Calls held'],
  ['no_shows', 'No shows'],
  ['proposals_sent', 'Proposals sent'],
  ['deals_created', 'Deals'],
  ['deals_won', 'Won'],
];
function percent(count: number, total: number): string {
  return total === 0 ? '-' : `${String(Math.round((count / total) * 100))}%`;
}

export function LinkedInFunnelPage({ client: givenClient }: { client?: SupabaseClient } = {}) {
  const client = useMemo(() => givenClient ?? createPublicSupabaseClient(), [givenClient]);
  const { token = '' } = useParams();
  const [tier, setTier] = useState<LinkedInTier>('tier2');
  const [payload, setPayload] = useState<LinkedInFunnel | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    try {
      const next = await loadLinkedInFunnel(client, token);
      setPayload(next);
      setError('');
    } catch (cause) {
      setPayload(null);
      setError(
        cause instanceof HuddleError && cause.kind === 'off'
          ? 'This link is turned off. Ask Kyu for a new one.'
          : 'The huddle could not be loaded. Try again in a moment.',
      );
    } finally {
      setLoading(false);
    }
  }, [client, token]);
  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refresh();
    }, 60_000);
    return () => window.clearInterval(timer);
  }, [refresh]);
  useEffect(() => {
    const title = document.title;
    document.title = 'LinkedIn funnel';
    let meta = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    const made = meta === null;
    if (meta === null) {
      meta = document.createElement('meta');
      meta.name = 'robots';
      document.head.append(meta);
    }
    const previous = meta.content;
    meta.content = 'noindex';
    return () => {
      document.title = title;
      if (made) meta?.remove();
      else if (meta) meta.content = previous;
    };
  }, []);
  const row = payload?.tiers.find((item) => item.tier === tier);
  const messaged = row?.messaged ?? 0;
  const weeks =
    payload?.weekly
      .filter((item) => item.tier === tier)
      .sort((a, b) => b.week_start.localeCompare(a.week_start)) ?? [];
  const messages = payload?.by_message.filter((item) => item.tier === tier) ?? [];
  return (
    <main className="huddle-page linkedin-funnel-page">
      <header className="huddle-header">
        <Link className="linkedin-funnel-link" to={`/huddle/${encodeURIComponent(token)}`}>
          Back to the Console
        </Link>
        <h1>LinkedIn funnel</h1>
        {payload && (
          <p>
            Updated{' '}
            {new Date(payload.generated_at).toLocaleTimeString('en-US', {
              timeZone: 'Asia/Manila',
              hour: 'numeric',
              minute: '2-digit',
            })}
          </p>
        )}
      </header>
      {loading && payload === null ? (
        <Skeleton lines={6} label="Loading LinkedIn funnel" />
      ) : error ? (
        <InlineError>{error}</InlineError>
      ) : (
        payload && (
          <>
            <div className="linkedin-funnel-tier" role="group" aria-label="Funnel tier">
              {(['tier2', 'tier1'] as const).map((key) => (
                <button
                  key={key}
                  type="button"
                  aria-pressed={tier === key}
                  onClick={() => setTier(key)}
                >
                  {key === 'tier2' ? 'Tier 2' : 'Tier 1'}
                </button>
              ))}
            </div>
            <section
              className="huddle-task"
              aria-label={`${tier === 'tier2' ? 'Tier 2' : 'Tier 1'} funnel`}
            >
              {tier === 'tier1' && <p>On hold until the Plastic Neutrality offer is ready.</p>}
              <p>
                Tagged: {row?.tagged ?? 0} · Not messaged yet: {row?.not_messaged ?? 0}
              </p>
              <dl className="linkedin-funnel-rows">
                {(tier === 'tier2' ? tier2Steps : tier1Steps).map(([key, label]) => {
                  const count = Number(row?.[key] ?? 0);
                  return (
                    <div key={key}>
                      <dt>{label}</dt>
                      <dd>
                        {count} {tier === 'tier2' && <span>{percent(count, messaged)}</span>}
                      </dd>
                    </div>
                  );
                })}
              </dl>
            </section>
            <section className="linkedin-funnel-stack" aria-label="This week and recent weeks">
              <h2>This week and recent weeks</h2>
              {weeks.map((week) => (
                <article key={week.week_start} className="huddle-task">
                  <h3>{weekLabel(week.week_start)}</h3>
                  <dl className="linkedin-funnel-rows">
                    {weekSteps
                      .filter(([key]) => Number(week[key]) > 0)
                      .map(([key, label]) => (
                        <div key={key}>
                          <dt>{label}</dt>
                          <dd>{week[key]}</dd>
                        </div>
                      ))}
                  </dl>
                </article>
              ))}
            </section>
            <section
              className="linkedin-funnel-stack"
              aria-label={payload.message_report?.performance_label ?? 'By message'}
            >
              <h2>{payload.message_report?.performance_label ?? 'By message'}</h2>
              {payload.message_report !== null && (
                <p>
                  {payload.message_report.scope}. {payload.message_report.performance_policy}
                </p>
              )}
              {payload.message_report !== null &&
                payload.message_report.attribution_needs_review > 0 && (
                  <p role="status">
                    Attribution needs review: {payload.message_report.attribution_needs_review} DM
                    entries are excluded.
                  </p>
                )}
              {messages.map((message, index) => (
                <article key={`${message.copy_set_name}-${String(index)}`} className="huddle-task">
                  <h3>
                    {payload.message_report === null
                      ? `${message.copy_set_name}${message.archived ? ' retired' : ''}`
                      : currentMessageLabel(message)}
                  </h3>
                  {payload.message_report !== null && <p>{messageVersionLabel(message)}</p>}
                  <p>
                    {message.people_messaged} messaged,{' '}
                    {(payload.message_report === null ? percent : messageRate)(
                      message.replied,
                      message.people_messaged,
                    )}{' '}
                    replied,{' '}
                    {(payload.message_report === null ? percent : messageRate)(
                      message.info_replied,
                      message.people_messaged,
                    )}{' '}
                    info
                  </p>
                </article>
              ))}
            </section>
            {payload.message_report !== null && (
              <section
                className="linkedin-funnel-stack"
                aria-label={payload.message_report.slot_label}
              >
                <h2>{payload.message_report.slot_label}</h2>
                <p>{payload.message_report.slot_policy}</p>
                {payload.message_report.slots
                  .filter((row) => row.tier === tier)
                  .map((row) => (
                    <article
                      className="huddle-task"
                      key={`${row.copy_set_id}:${row.message_version}:${row.message_slot}`}
                    >
                      <h3>{currentMessageLabel(row)}</h3>
                      <p>{messageVersionLabel(row)}</p>
                      <p>
                        {row.copy_set_id === null
                          ? 'Message not recorded'
                          : row.message_slot === null
                            ? 'Slot not recorded'
                            : `Slot ${row.message_slot}`}{' '}
                        · {row.dms_sent} DMs · {row.people_messaged} people
                      </p>
                    </article>
                  ))}
              </section>
            )}
            <footer>
              <p>Counts are people and events logged in the Hub. Not a promise of results.</p>
            </footer>
          </>
        )
      )}
    </main>
  );
}
